import { Theme } from '@mui/material';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useSelector } from 'react-redux';
import { selectExpandedModsDialog, selectIsEditMod, selectStandard } from 'store/redux-store/placement-mods/selectors';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<{ isExpanded?: boolean }>()((theme: Theme, { isExpanded }) => ({
  inputFields: {
    minWidth: isExpanded ? '264px' : '211px',
    '& .MuiFilledInput-input': {
      height: '2em',
    },
  },
}));

interface EffectiveStartDateProps {
  readOnly?: boolean;
}

export const EffectiveStartDate = ({ readOnly }: EffectiveStartDateProps) => {
  const isExpanded = useSelector(selectExpandedModsDialog);
  const { classes } = useStyles({ isExpanded });
  const { t } = useTranslation();
  const { control, formState: { errors }, setError, clearErrors, setValue } = useFormContext();
  const value: Date | string | null | undefined = useWatch({ name: 'startDate' });
  const endDateWatch: Date | string | null | undefined = useWatch({ name: 'endDate' });
  const isModEdit = useSelector(selectIsEditMod);
  const standard = useSelector(selectStandard);
  const { promiseInProgress: isSaveModApiCalling } = usePromiseTracker({ area: 'save-mod-api', delay: 0 });
  const [isFieldRequired, setFieldRequired] = useState<boolean>(false);

  const handleChange = (param: Date | string | null | undefined) => {
    if (param) {
      setValue('startDate', param);
      setFieldRequired(false);
      clearErrors('startDate');
    } else {
      if (endDateWatch) {
        setValue('startDate', null);
        setFieldRequired(true);
        setError('startDate', { type: 'required', message: 'Required' });
      } else {
        setValue('startDate', null);
        setFieldRequired(false);
        clearErrors('startDate');
      }
    }
  };

  useEffect(() => {
    if (endDateWatch && value == null) {
      handleChange(value);
    } else {
      handleChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDateWatch, value, isFieldRequired]);

  return (
    <Controller
      control={control}
      name={'startDate'}
      rules={{
        required: isFieldRequired,
      }}
      render={({ field: { onChange, value, ...rest } }) => {
        const startDate = moment(value ?? null);
        return (
          <React.Fragment>
            <DateRangePicker
              onChange={newValue => {
                if (newValue.startDate !== undefined) {
                  const newDate = moment(newValue.startDate);
                  const setTo = newDate.isValid() ? moment(newDate).format('YYYY-MM-DDTHH:mm:ss') : null;
                  onChange(setTo);
                  handleChange(setTo);
                } else {
                  onChange(null);
                  handleChange(null);
                }
              }}
              value={value}
              maxDate={
                endDateWatch ? new Date(moment(endDateWatch).subtract(1, 'day').format('YYYY-MM-DDTHH:mm:ss')) : null
              }
              quickSelect={false}
              onCancel={() => {}}
              clear
              open={false}
              variant="filled"
              single
              placeholder={t('placement.placementMods.effectiveStartDate')}
              useMaxWidth={true}
              showIcon={true}
              className={classes.inputFields}
              labelClass={classes.inputFields}
              actionBar={false}
              clickAway={true}
              trailingIconShow={true}
              readOnly={readOnly !== undefined ? readOnly : (standard ? true : !isModEdit)}
              isDisabled={isSaveModApiCalling}
              keepClickAwayVal={false}
              initialDateRange={{
                startDate: startDate.isValid() ? new Date(startDate.format('YYYY-MM-DDTHH:mm:ss')) : undefined,
                endDate: startDate.isValid() ? new Date(startDate.format('YYYY-MM-DDTHH:mm:ss')) : undefined,
                useLabelAsValue: true,
              }}
              {...rest}
              error={isFieldRequired}
              helperText={!isNullOrUndefined(errors?.startDate) ? (errors?.startDate?.message as string) || '' : undefined}
            />
          </React.Fragment>
        );
      }}
    />
  );
};
